import React, { useEffect, useState } from "react";
import {
  Megaphone, Send, Mail, Image as ImageIcon, Link as LinkIcon,
  CheckCircle2, AlertCircle, Loader2, ArrowLeft, Users,
  Clock, RefreshCw, Sparkles, Plus, Trash2, UserPlus, Upload, X, Eye,
} from "lucide-react";
import { authFetch } from "../utils/adminAuth";
import AdminShell, { StatCard } from "../components/admin/AdminShell";

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const StatusPill = ({ status }) => {
  const map = {
    sending: "bg-amber-50 text-amber-700 border-amber-200",
    sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    partial: "bg-orange-50 text-orange-700 border-orange-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${map[status] || map.sending}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {status}
    </span>
  );
};

const AdminCampaignsPage = () => {
  const [view, setView] = useState("list"); // list | compose | recipients
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ eligible_recipients: 0, total_publishers: 0, manual_recipients: 0, combined_unique: 0, unsubscribed: 0 });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [listResp, countsResp] = await Promise.all([
        authFetch("/api/admin/campaigns"),
        authFetch("/api/admin/campaigns/recipients/count"),
      ]);
      if (listResp.ok) setCampaigns(await listResp.json());
      if (countsResp.ok) setCounts(await countsResp.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  return (
    <AdminShell
      pageTitle="Offer Broadcasts"
      pageDescription="Send promotional offers to every publisher in your network."
      rightSlot={
        <button
          onClick={fetchAll}
          className="flex items-center gap-1.5 px-3 h-9 rounded-md text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={2.25} />
          Refresh
        </button>
      }
    >
      {view === "list" ? (
        <CampaignList
          campaigns={campaigns}
          loading={loading}
          counts={counts}
          onNew={() => setView("compose")}
          onManageRecipients={() => setView("recipients")}
        />
      ) : view === "compose" ? (
        <Composer
          counts={counts}
          onCancel={() => setView("list")}
          onSent={async () => { await fetchAll(); setView("list"); }}
        />
      ) : (
        <RecipientManager
          onClose={async () => { await fetchAll(); setView("list"); }}
        />
      )}
    </AdminShell>
  );
};


// ---------- List View ----------
const CampaignList = ({ campaigns, loading, counts, onNew, onManageRecipients }) => {
  const [failuresFor, setFailuresFor] = useState(null); // campaign id being inspected
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [viewFor, setViewFor] = useState(null); // campaign id being previewed
  const [resendFor, setResendFor] = useState(null); // campaign object pending confirmation
  const [resending, setResending] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4500);
  };

  const runDiagnostics = async () => {
    setDiagLoading(true);
    setDiagnostics({ loading: true });
    try {
      const r = await authFetch("/api/admin/campaigns/diagnostics/resend");
      const d = await r.json();
      setDiagnostics(d);
    } catch (e) {
      setDiagnostics({ error: e.message || String(e) });
    } finally {
      setDiagLoading(false);
    }
  };

  const doResend = async () => {
    if (!resendFor) return;
    setResending(true);
    try {
      const r = await authFetch(`/api/admin/campaigns/${resendFor.id}/resend`, {
        method: "POST",
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Resend failed");
      showToast(
        "success",
        `Resent to ${data.sent_count} recipients${data.failed_count ? ` (${data.failed_count} failed)` : ""}.`
      );
      setResendFor(null);
      // Reload the list so the new campaign appears.
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      showToast("error", e.message || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  return (
  <>
    {/* Stats — Swiss grid */}
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
      <StatCard label="Unique Emails" value={counts.total_publishers} icon={Users} tone="info" />
      <StatCard label="Manual List" value={counts.manual_recipients} icon={Plus} tone="violet" />
      <StatCard label="Eligible to Receive" value={counts.eligible_recipients} icon={Mail} tone="success" />
      <StatCard label="Unsubscribed" value={counts.unsubscribed} icon={AlertCircle} tone="danger" />
      <StatCard label="Campaigns Sent" value={campaigns.filter(c => c.status !== 'sending').length} icon={Megaphone} tone="neutral" />
    </div>

    {/* Action bar */}
    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
      <h2 className="font-display text-[15px] font-semibold text-slate-900 tracking-tight">Campaign History</h2>
      <div className="flex gap-2">
        <button
          onClick={onManageRecipients}
          className="flex items-center gap-2 px-3 h-9 rounded-md bg-white hover:bg-gray-50 border border-gray-300 text-slate-700 font-medium text-[13px] transition shadow-sm"
          data-testid="manage-recipients-btn"
        >
          <UserPlus className="w-3.5 h-3.5" strokeWidth={2.25} />
          Manage Recipients
        </button>
        <button
          onClick={onNew}
          className="flex items-center gap-2 px-3.5 h-9 rounded-md bg-slate-900 hover:bg-slate-800 text-white font-semibold text-[13px] shadow-sm hover:shadow transition"
          data-testid="new-campaign-btn"
        >
          <Sparkles className="w-3.5 h-3.5" strokeWidth={2.25} />
          New Campaign
        </button>
      </div>
    </div>

    {/* List */}
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
      {loading && campaigns.length === 0 && (
        <div className="text-center py-16 text-slate-500 text-sm">Loading…</div>
      )}
      {!loading && campaigns.length === 0 && (
        <div className="text-center py-16 px-4">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Megaphone className="w-6 h-6 text-slate-400" strokeWidth={2} />
          </div>
          <p className="text-slate-700 font-semibold">No campaigns yet</p>
          <p className="text-slate-500 text-sm mt-1">Create your first broadcast to get started.</p>
        </div>
      )}
      {campaigns.length > 0 && (
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Subject</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Sent</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Recipients</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Delivered</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Opens</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Status</th>
              <th className="text-right px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {campaigns.map((c) => {
              const hasFailures = (c.failed_count || 0) > 0;
              return (
              <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-5 py-3.5">
                  <p className="font-medium text-slate-900 text-sm">{c.subject}</p>
                  <p className="text-xs text-slate-500 mt-0.5">by {c.created_by}</p>
                </td>
                <td className="px-5 py-3.5 text-sm text-slate-700 tabular-nums">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3 text-slate-400" strokeWidth={2} />
                    {formatDate(c.sent_at || c.created_at)}
                  </div>
                </td>
                <td className="px-5 py-3.5 text-sm text-slate-700 tabular-nums">{c.total_recipients}</td>
                <td className="px-5 py-3.5 text-sm tabular-nums">
                  <span className="text-emerald-700 font-semibold">{c.sent_count}</span>
                  {hasFailures && (
                    <button
                      onClick={() => setFailuresFor(c.id)}
                      className="text-red-700 hover:text-red-800 underline decoration-dotted underline-offset-2 ml-1 font-semibold"
                      data-testid={`campaign-failures-${c.id}`}
                      title="Click to see why these failed"
                    >
                      · {c.failed_count} failed
                    </button>
                  )}
                  {(c.bounced_removed_count || 0) > 0 && (
                    <span
                      className="ml-1 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500"
                      title="Invalid emails auto-removed from the mailing list via bounce webhooks"
                      data-testid={`campaign-bounced-${c.id}`}
                    >
                      · <span className="text-amber-600 font-semibold">{c.bounced_removed_count}</span> removed
                    </span>
                  )}
                </td>
                <td className="px-5 py-3.5 text-sm tabular-nums" data-testid={`campaign-opens-${c.id}`}>
                  {(() => {
                    const opens = c.opens_count || 0;
                    const sent = c.sent_count || 0;
                    const rate = sent > 0 ? Math.round((opens / sent) * 100) : 0;
                    return opens > 0 ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-blue-700 font-semibold">{opens}</span>
                        <span className="text-[11px] text-slate-500">({rate}%)</span>
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    );
                  })()}
                </td>
                <td className="px-5 py-3.5"><StatusPill status={c.status} /></td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => setViewFor(c.id)}
                      className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-xs font-semibold transition"
                      data-testid={`campaign-view-${c.id}`}
                      title="Preview the email that was sent"
                    >
                      <Eye className="w-3.5 h-3.5" strokeWidth={2.25} />
                      View
                    </button>
                    <button
                      onClick={() => setResendFor(c)}
                      disabled={c.status === "sending"}
                      className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold transition"
                      data-testid={`campaign-resend-${c.id}`}
                      title="Send this exact same email again"
                    >
                      <Send className="w-3.5 h-3.5" strokeWidth={2.25} />
                      Resend
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>

    {/* Failures modal */}
    {failuresFor && (
      <FailuresModal
        campaignId={failuresFor}
        onClose={() => setFailuresFor(null)}
        onRetried={() => window.location.reload()}
      />
    )}

    {/* View / preview modal */}
    {viewFor && (
      <ViewCampaignModal campaignId={viewFor} onClose={() => setViewFor(null)} />
    )}

    {/* Resend confirmation modal */}
    {resendFor && (
      <ResendConfirmModal
        campaign={resendFor}
        resending={resending}
        onCancel={() => setResendFor(null)}
        onConfirm={doResend}
      />
    )}

    {/* Toast */}
    {toast && (
      <div className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-xl border px-4 py-3 shadow-lg flex items-start gap-3 ${
        toast.type === "success"
          ? "bg-emerald-50 border-emerald-300 text-emerald-800"
          : "bg-rose-50 border-rose-300 text-rose-800"
      }`}>
        {toast.type === "success" ? <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
        <p className="text-sm">{toast.message}</p>
      </div>
    )}

    {/* Diagnostics modal */}
    {diagnostics && (
      <DiagnosticsModal data={diagnostics} onClose={() => setDiagnostics(null)} />
    )}

    {/* Floating diagnostic button — only show if at least one failed campaign exists */}
    {campaigns.some(c => (c.failed_count || 0) > 0) && (
      <button
        onClick={runDiagnostics}
        disabled={diagLoading}
        title="Diagnose why emails failed (checks Resend API key, sender domain, and last error)"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow-xl shadow-red-600/30 ring-1 ring-red-700/50 transition disabled:opacity-60"
        data-testid="resend-diagnostics-btn"
      >
        {diagLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
        Run Resend Diagnostic
      </button>
    )}
  </>
  );
};


// ---------- Composer ----------
const SIGNUP_URL = "https://digitalmastermedia.com/apply";

const Composer = ({ counts, onCancel, onSent }) => {
  const [form, setForm] = useState({
    subject: "",
    preheader: "",
    hero_image_url: "",
    body_text: "",
    cta_text: "Apply / Claim this offer →",
    cta_url: SIGNUP_URL,
  });
  // Recipient targeting — "all" keeps the original behaviour (every publisher
  // in the panel), "custom" sends only to addresses pasted into the textarea.
  const [recipientMode, setRecipientMode] = useState("all");
  const [customEmailsText, setCustomEmailsText] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState(null);

  // Parse the custom-emails textarea into a clean, de-duplicated, validated
  // address list. Accepts one-per-line, comma-, or semicolon-separated input.
  const parseCustomEmails = (text) => {
    const valid = [];
    const invalid = [];
    const seen = new Set();
    (text || "")
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
      .forEach((e) => {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
          invalid.push(e);
        } else if (!seen.has(e)) {
          seen.add(e);
          valid.push(e);
        }
      });
    return { valid, invalid };
  };

  const parsedCustom = parseCustomEmails(customEmailsText);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4500);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadHeroImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast("error", "Please choose an image file (JPG, PNG, GIF, WEBP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast("error", "Image too large (max 5 MB).");
      return;
    }
    setUploadingImage(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const resp = await authFetch("/api/admin/campaigns/upload-image", {
        method: "POST",
        body, // let browser set the multipart boundary
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || "Upload failed");
      setForm((f) => ({ ...f, hero_image_url: data.url }));
      showToast("success", "Hero image uploaded.");
    } catch (e) {
      showToast("error", e.message || "Upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const clearHeroImage = () => setForm((f) => ({ ...f, hero_image_url: "" }));

  // Convert simple text body into HTML paragraphs (publishers paste plain text; we keep it safe)
  const bodyAsHtml = () => {
    const safe = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return (form.body_text || "")
      .split(/\n\s*\n/)
      .map((para) =>
        `<p style="margin:0 0 14px 0;">${safe(para).replace(/\n/g, "<br>")}</p>`
      ).join("");
  };

  const payload = () => {
    const base = {
      subject: form.subject.trim(),
      preheader: form.preheader.trim() || undefined,
      hero_image_url: form.hero_image_url.trim() || undefined,
      body_html: bodyAsHtml(),
      cta_text: form.cta_text.trim() || undefined,
      cta_url: form.cta_url.trim() || undefined,
      recipient_mode: recipientMode,
    };
    if (recipientMode === "custom") {
      base.custom_recipients = parsedCustom.valid;
    }
    return base;
  };

  const customCount = parsedCustom.valid.length;
  // Send button is only enabled when subject + body are filled AND, in custom
  // mode, at least one valid email has been entered.
  const canSend =
    form.subject.trim().length > 0 &&
    form.body_text.trim().length > 0 &&
    (recipientMode !== "custom" || customCount > 0);
  const eligibleCount =
    recipientMode === "custom" ? customCount : counts.eligible_recipients;

  const sendTest = async () => {
    if (!testEmail.includes("@")) {
      showToast("error", "Please enter a valid test email address.");
      return;
    }
    if (!canSend) {
      showToast("error", "Subject and body are required before testing.");
      return;
    }
    setTesting(true);
    try {
      const resp = await authFetch("/api/admin/campaigns/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload(), test_email: testEmail.trim() }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || "Test send failed");
      showToast("success", `Test sent to ${testEmail}`);
    } catch (e) {
      showToast("error", e.message || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const sendToAll = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const resp = await authFetch("/api/admin/campaigns", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload()),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || "Send failed");
      showToast(
        "success",
        `Sent to ${data.sent_count} ${recipientMode === "custom" ? "recipients" : "publishers"}${data.failed_count ? ` (${data.failed_count} failed)` : ""}.`
      );
      setTimeout(() => onSent(), 1500);
    } catch (e) {
      showToast("error", e.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Composer form */}
      <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white/[0.03] backdrop-blur-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={onCancel}
            className="p-2 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h2 className="text-xl font-semibold">Compose Campaign</h2>
        </div>

        <div className="space-y-4">
          <Field label="Subject *">
            <input
              type="text"
              value={form.subject}
              onChange={set("subject")}
              placeholder="e.g. New Solar Offer — $85 per call"
              className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
              data-testid="campaign-subject"
            />
          </Field>

          <Field label="Preheader (preview text shown in inbox list — optional)">
            <input
              type="text"
              value={form.preheader}
              onChange={set("preheader")}
              placeholder="e.g. Priority access for the next 48 hours"
              className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
            />
          </Field>

          <Field label={<><ImageIcon className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />Hero image (optional)</>}>
            {form.hero_image_url ? (
              <div className="relative rounded-lg border border-gray-200 bg-white/[0.03] overflow-hidden group">
                <img
                  src={form.hero_image_url}
                  alt="Hero preview"
                  className="w-full max-h-56 object-contain bg-black/30"
                  data-testid="hero-image-preview"
                />
                <div className="flex items-center justify-between px-3 py-2 bg-black/40 text-xs">
                  <span className="text-slate-400 font-mono truncate pr-3" title={form.hero_image_url}>
                    {form.hero_image_url}
                  </span>
                  <button
                    type="button"
                    onClick={clearHeroImage}
                    className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-200 transition"
                    data-testid="hero-image-remove"
                  >
                    <X className="w-3 h-3" /> Remove
                  </button>
                </div>
              </div>
            ) : (
              <label
                htmlFor="hero-image-upload"
                className={`flex flex-col items-center justify-center px-4 py-6 rounded-lg border-2 border-dashed ${
                  uploadingImage
                    ? "border-purple-500/40 bg-purple-500/5 cursor-wait"
                    : "border-gray-300 bg-gray-50 hover:border-purple-500/40 hover:bg-purple-500/5 cursor-pointer"
                } transition`}
                data-testid="hero-image-dropzone"
              >
                {uploadingImage ? (
                  <>
                    <Loader2 className="w-6 h-6 text-slate-700 animate-spin mb-2" />
                    <p className="text-sm text-slate-600">Uploading…</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-slate-400 mb-2" />
                    <p className="text-sm text-slate-700 font-medium">Click to upload an image</p>
                    <p className="text-xs text-slate-500 mt-1">JPG, PNG, GIF or WEBP · max 5 MB</p>
                  </>
                )}
                <input
                  id="hero-image-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  disabled={uploadingImage}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = ""; // allow re-selecting same file later
                    uploadHeroImage(f);
                  }}
                  data-testid="hero-image-input"
                />
              </label>
            )}
            <details className="mt-2 text-xs text-slate-500 group">
              <summary className="cursor-pointer hover:text-slate-400 transition select-none">
                Or paste an image URL instead
              </summary>
              <input
                type="url"
                value={form.hero_image_url}
                onChange={set("hero_image_url")}
                placeholder="https://your-cdn.com/offer-banner.jpg"
                className="w-full mt-2 px-4 py-2 rounded-lg bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 font-mono text-xs"
                data-testid="hero-image-url"
              />
            </details>
          </Field>

          <Field label="Message body *">
            <textarea
              value={form.body_text}
              onChange={set("body_text")}
              rows={10}
              placeholder={"Hi there,\n\nWe have a new $100/call solar leads offer going live this week — US Nationwide, English speakers only.\n\nPriority access is limited to publishers who have been active in the last 30 days."}
              className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 leading-relaxed"
              data-testid="campaign-body"
            />
            <p className="text-xs text-slate-500 mt-1.5">
              Double line-break = new paragraph. Keep it short & skimmable. HTML is escaped for safety.
            </p>
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={<><LinkIcon className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />CTA button text</>}>
              <input
                type="text"
                value={form.cta_text}
                onChange={set("cta_text")}
                placeholder="e.g. Claim priority access →"
                className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
              />
            </Field>
            <Field label="CTA URL">
              <input
                type="url"
                value={form.cta_url}
                onChange={set("cta_url")}
                placeholder="https://digitalmastermedia.com/apply"
                className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 font-mono text-sm"
                data-testid="campaign-cta-url"
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Defaults to publisher signup. Anyone clicking the CTA will land on the application form.
                </p>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, cta_url: SIGNUP_URL }))}
                  className="text-[11px] text-slate-700 hover:text-purple-200 underline decoration-dotted whitespace-nowrap ml-3"
                  data-testid="reset-cta-signup-btn"
                >
                  Reset to signup
                </button>
              </div>
            </Field>
          </div>
        </div>
      </div>

      {/* Side panel: recipient targeting + test send + big send button */}
      <div className="space-y-4">
        {/* "Send To" picker — radios let admin choose between blasting all
            publishers in the panel OR a custom list of pasted emails. */}
        <div
          className="rounded-2xl border border-gray-200 bg-white/[0.03] backdrop-blur-sm p-6"
          data-testid="recipient-mode-card"
        >
          <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" />
            Send To
          </h3>
          <div className="space-y-2 mb-3">
            <label
              className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition ${
                recipientMode === "all"
                  ? "border-emerald-300 bg-emerald-50"
                  : "border-gray-200 hover:border-purple-300 hover:bg-purple-50/40"
              }`}
              data-testid="recipient-mode-all-row"
            >
              <input
                type="radio"
                name="recipient-mode"
                value="all"
                checked={recipientMode === "all"}
                onChange={() => setRecipientMode("all")}
                className="mt-0.5 w-4 h-4 cursor-pointer accent-emerald-600"
                data-testid="recipient-mode-all"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 leading-tight">All Publishers</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Send to every publisher in the panel ({counts.eligible_recipients} eligible after unsubscribes).
                </p>
              </div>
            </label>
            <label
              className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition ${
                recipientMode === "custom"
                  ? "border-emerald-300 bg-emerald-50"
                  : "border-gray-200 hover:border-purple-300 hover:bg-purple-50/40"
              }`}
              data-testid="recipient-mode-custom-row"
            >
              <input
                type="radio"
                name="recipient-mode"
                value="custom"
                checked={recipientMode === "custom"}
                onChange={() => setRecipientMode("custom")}
                className="mt-0.5 w-4 h-4 cursor-pointer accent-emerald-600"
                data-testid="recipient-mode-custom"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 leading-tight">Custom Email Send</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Send only to the email addresses you enter below.
                </p>
              </div>
            </label>
          </div>

          {/* Custom emails textarea — appears only when "Custom Email Send" picked */}
          {recipientMode === "custom" && (
            <div className="mt-3" data-testid="custom-emails-block">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5 block">
                Recipient Emails
              </label>
              <textarea
                value={customEmailsText}
                onChange={(e) => setCustomEmailsText(e.target.value)}
                placeholder={"abc@gmail.com\njohn@gmail.com\npublisher@gmail.com"}
                rows={8}
                className="w-full px-3 py-2 rounded-lg bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 text-sm font-mono leading-relaxed resize-y"
                data-testid="custom-emails-textarea"
              />
              <div className="flex items-center justify-between mt-2 text-xs">
                <span className="text-slate-500">
                  One per line, or paste comma-/semicolon-separated.
                </span>
                <span
                  className={`font-bold ${customCount > 0 ? "text-emerald-700" : "text-slate-400"}`}
                  data-testid="custom-emails-count"
                >
                  {customCount} valid
                  {parsedCustom.invalid.length > 0 && (
                    <span className="text-rose-600 font-semibold ml-1.5">
                      · {parsedCustom.invalid.length} invalid skipped
                    </span>
                  )}
                </span>
              </div>
              {customCount === 0 && customEmailsText.trim().length > 0 && (
                <p className="text-xs text-rose-600 mt-1" data-testid="custom-emails-empty-warning">
                  None of these addresses look like valid emails. Please check the format.
                </p>
              )}
            </div>
          )}

          {/* Always-visible totals strip */}
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-1 text-sm">
            {recipientMode === "all" ? (
              <>
                <div className="flex justify-between">
                  <span className="text-slate-400">Total publishers</span>
                  <span className="text-slate-900 font-semibold">{counts.total_publishers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Unsubscribed</span>
                  <span className="text-rose-600 font-semibold">{counts.unsubscribed}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between">
                <span className="text-slate-400">Custom emails</span>
                <span className="text-slate-900 font-semibold">{customCount}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-100 mt-2">
              <span className="text-slate-700 font-semibold">Will receive</span>
              <span
                className="text-emerald-700 font-bold text-base"
                data-testid="will-receive-count"
              >
                {eligibleCount}
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white/[0.03] backdrop-blur-sm p-6">
          <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-slate-700" />
            Send test email
          </h3>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="you@digitalmastermedia.com"
            className="w-full px-3 py-2 rounded-lg bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 text-sm mb-3"
            data-testid="campaign-test-email"
          />
          <button
            onClick={sendTest}
            disabled={testing || !canSend}
            className="w-full py-2 rounded-lg bg-gray-100 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium flex items-center justify-center gap-2 transition"
            data-testid="campaign-send-test"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {testing ? "Sending…" : "Send test to me"}
          </button>
        </div>

        <button
          onClick={() => setConfirmOpen(true)}
          disabled={!canSend || sending || eligibleCount === 0}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-800 hover:from-purple-600 hover:to-pink-700 disabled:from-slate-700 disabled:to-slate-800 disabled:text-slate-500 disabled:cursor-not-allowed text-white font-bold shadow-lg shadow-purple-900/40 flex items-center justify-center gap-2 transition"
          data-testid="campaign-send-all"
        >
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          {sending
            ? "Sending…"
            : recipientMode === "custom"
              ? `Send to ${customCount} ${customCount === 1 ? "email" : "emails"}`
              : `Send to ${counts.eligible_recipients} publishers`}
        </button>
      </div>

      {/* Confirmation modal */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="rounded-2xl border border-gray-200 bg-white max-w-md w-full p-6 shadow-xl">
            <div className="h-1 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 mb-4 -mt-6 -mx-6 rounded-t-2xl" />
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-purple-500/20 border border-purple-500/40 flex items-center justify-center">
                <Send className="w-5 h-5 text-slate-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Send broadcast?</h3>
                <p className="text-sm text-slate-400 mt-0.5">This cannot be undone.</p>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-5 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-400">Send to:</span>
                <span className="font-semibold text-slate-900" data-testid="confirm-mode-label">
                  {recipientMode === "custom"
                    ? `${customCount} Custom ${customCount === 1 ? "Email" : "Emails"}`
                    : "All Publishers"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Recipients:</span>
                <span className="font-semibold text-emerald-700" data-testid="confirm-recipient-count">
                  {eligibleCount}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Subject:</span>
                <span className="text-slate-700 max-w-[240px] truncate">{form.subject}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                className="flex-1 py-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200 text-slate-700 text-sm font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={sendToAll}
                className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-slate-900 to-slate-800 hover:from-purple-600 hover:to-pink-700 text-white font-semibold text-sm transition"
                data-testid="campaign-confirm-send"
              >
                Yes, send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-xl border backdrop-blur-sm px-4 py-3 shadow-lg flex items-start gap-3 ${
          toast.type === "success"
            ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-100"
            : "bg-rose-500/10 border-rose-500/40 text-rose-100"
        }`}>
          {toast.type === "success" ? <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
          <p className="text-sm">{toast.message}</p>
        </div>
      )}
    </div>
  );
};


// ---------- Recipient Manager ----------
const RecipientManager = ({ onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkInput, setBulkInput] = useState("");
  const [label, setLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const load = async () => {
    setLoading(true);
    try {
      const resp = await authFetch("/api/admin/campaigns/recipients/manual");
      if (resp.ok) setItems(await resp.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addEmails = async () => {
    if (!bulkInput.trim()) return;
    setAdding(true);
    try {
      const resp = await authFetch("/api/admin/campaigns/recipients/manual", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: bulkInput, label: label.trim() || undefined }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || "Add failed");
      const msgs = [];
      if (data.added) msgs.push(`Added ${data.added}`);
      if (data.skipped_existing) msgs.push(`${data.skipped_existing} already present`);
      if (data.invalid?.length) msgs.push(`${data.invalid.length} invalid`);
      showToast("success", msgs.join(" · ") || "Done");
      setBulkInput("");
      await load();
    } catch (e) {
      showToast("error", e.message || "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const removeEmail = async (email) => {
    if (!window.confirm(`Remove ${email} from additional recipients?`)) return;
    try {
      const qs = new URLSearchParams({ email });
      const resp = await authFetch(`/api/admin/campaigns/recipients/manual?${qs}`, { method: "DELETE" });
      if (!resp.ok) throw new Error("Delete failed");
      await load();
    } catch (e) {
      showToast("error", e.message);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Add form */}
      <div className="lg:col-span-1 rounded-2xl border border-gray-200 bg-white/[0.03] backdrop-blur-sm p-6 h-fit">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={onClose} className="p-2 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h2 className="text-lg font-semibold">Add Recipients</h2>
        </div>

        <Field label="Emails">
          <textarea
            value={bulkInput}
            onChange={(e) => setBulkInput(e.target.value)}
            rows={8}
            placeholder={"Paste one email per line, or comma-separated:\n\njane@company.com\npeter@example.com\n..."}
            className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-sm font-mono"
            data-testid="recipients-bulk-input"
          />
          <p className="text-xs text-slate-500 mt-1.5">
            Invalid entries are reported and skipped. Duplicates are ignored.
          </p>
        </Field>

        <Field label="Label (optional)">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Pre-launch waitlist"
            className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
          />
        </Field>

        <button
          onClick={addEmails}
          disabled={adding || !bulkInput.trim()}
          className="w-full mt-4 py-2.5 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm flex items-center justify-center gap-2 transition"
          data-testid="recipients-add-btn"
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {adding ? "Adding…" : "Add to list"}
        </button>

        <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs text-slate-400 leading-relaxed">
          💡 Manually added emails are saved permanently and included on <strong>every</strong> future broadcast
          (unless they later unsubscribe). Publisher emails from your application database are already included
          automatically — no need to add them here.
        </div>
      </div>

      {/* List */}
      <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white/[0.03] backdrop-blur-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-700">Additional Recipients ({items.length})</h3>
        </div>
        {loading && items.length === 0 && (
          <div className="text-center py-12 text-slate-500">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center py-12 px-4">
            <UserPlus className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No extra recipients yet</p>
            <p className="text-slate-500 text-sm mt-1">Publisher emails from your database are still included on every send.</p>
          </div>
        )}
        {items.length > 0 && (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Email</th>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Label</th>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Added</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((r) => (
                <tr key={r.email} className="hover:bg-gray-50">
                  <td className="px-5 py-3 text-sm text-slate-900 font-mono">{r.email}</td>
                  <td className="px-5 py-3 text-sm text-slate-600">{r.label || <span className="text-slate-500">—</span>}</td>
                  <td className="px-5 py-3 text-xs text-slate-400">
                    {r.added_at ? new Date(r.added_at).toLocaleDateString() : "—"}
                    {r.added_by && <div className="text-[10px] text-slate-500">by {r.added_by}</div>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => removeEmail(r.email)}
                      className="p-1.5 rounded-lg text-rose-400 hover:text-white hover:bg-rose-500/20 border border-transparent hover:border-rose-500/30 transition"
                      title="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-xl border backdrop-blur-sm px-4 py-3 shadow-lg flex items-start gap-3 ${
          toast.type === "success"
            ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-100"
            : "bg-rose-500/10 border-rose-500/40 text-rose-100"
        }`}>
          {toast.type === "success" ? <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
          <p className="text-sm">{toast.message}</p>
        </div>
      )}
    </div>
  );
};


const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">{label}</label>
    {children}
  </div>
);


// ---------- Failures Modal ----------
const FailuresModal = ({ campaignId, onClose, onRetried }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null);

  const load = async () => {
    try {
      const r = await authFetch(`/api/admin/campaigns/${campaignId}/failures`);
      if (!r.ok) throw new Error((await r.json()).detail || "Failed to load");
      setData(await r.json());
    } catch (e) {
      setErr(e.message || String(e));
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const retryFailed = async () => {
    if (!data?.failed_count) return;
    setRetrying(true);
    setRetryResult(null);
    try {
      const r = await authFetch(`/api/admin/campaigns/${campaignId}/retry-failed`, {
        method: "POST",
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || "Retry failed");
      setRetryResult(body);
      // Refresh the failure list so the UI shows the updated state.
      await load();
      if (onRetried) onRetried();
    } catch (e) {
      setRetryResult({ error: e.message || String(e) });
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="rounded-2xl border border-gray-200 bg-white max-w-2xl w-full max-h-[85vh] overflow-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="failures-modal"
      >
        <div className="h-1 bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500" />
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-rose-200">Delivery Failures</h3>
              <p className="text-xs text-slate-500 mt-0.5">Resend error messages grouped by root cause.</p>
            </div>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
          </div>
          {!data && !err && (
            <div className="py-8 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…
            </div>
          )}
          {err && (
            <div className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm">{err}</div>
          )}
          {data && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                <div className="rounded-lg bg-white border border-gray-300 py-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Total</p>
                  <p className="text-xl font-bold text-slate-900">{data.total_recipients}</p>
                </div>
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 py-3">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-400">Sent</p>
                  <p className="text-xl font-bold text-emerald-300">{data.sent_count}</p>
                </div>
                <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 py-3">
                  <p className="text-[10px] uppercase tracking-wider text-rose-400">Failed</p>
                  <p className="text-xl font-bold text-rose-300">{data.failed_count}</p>
                </div>
              </div>

              {data.failed_count > 0 && (
                <button
                  onClick={retryFailed}
                  disabled={retrying}
                  className="w-full mb-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 disabled:from-slate-700 disabled:to-slate-800 disabled:cursor-not-allowed text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/30 transition"
                  data-testid="retry-failed-btn"
                >
                  {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {retrying ? `Retrying ${data.failed_count} recipients…` : `Retry ${data.failed_count} Failed Sends`}
                </button>
              )}

              {retryResult && !retryResult.error && (
                <div className="mb-4 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-100 text-sm">
                  Retried {retryResult.retried}. Newly sent: <strong>{retryResult.newly_sent}</strong>
                  {retryResult.still_failed > 0 && <> · Still failing: <strong>{retryResult.still_failed}</strong></>}
                </div>
              )}
              {retryResult?.error && (
                <div className="mb-4 p-3 rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm">
                  {retryResult.error}
                </div>
              )}

              {data.error_groups?.length ? (
                <div className="space-y-3">
                  {data.error_groups.map((g, i) => (
                    <div key={i} className="rounded-lg border border-rose-500/25 bg-rose-500/5 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs uppercase tracking-wider text-rose-300 font-semibold">{g.count} recipients affected</p>
                      </div>
                      <p className="font-mono text-xs text-rose-100 bg-black/30 p-2 rounded break-words">{g.error}</p>
                      <p className="text-[11px] text-slate-500 mt-2">
                        Sample: {g.sample_emails.join(", ")}{g.count > g.sample_emails.length ? "…" : ""}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-6">No failures recorded for this campaign.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// ---------- Diagnostics Modal ----------
const DiagnosticsModal = ({ data, onClose }) => {
  const verdict = data?.verdict || "Running diagnostics…";
  const isOk = typeof verdict === "string" && /valid|should work/i.test(verdict);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="rounded-2xl border border-gray-200 bg-white max-w-2xl w-full max-h-[85vh] overflow-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="diagnostics-modal"
      >
        <div className={`h-1 bg-gradient-to-r ${isOk ? "from-emerald-500 to-cyan-500" : "from-amber-500 to-rose-500"}`} />
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {isOk ? <CheckCircle2 className="w-6 h-6 text-emerald-400" /> : <AlertCircle className="w-6 h-6 text-amber-400" />}
              <div>
                <h3 className="text-lg font-bold">Resend Diagnostics</h3>
                <p className="text-xs text-slate-500 mt-0.5">Live check of the email sending infrastructure.</p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
          </div>

          <div className={`p-4 rounded-lg border text-sm mb-4 ${isOk ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-100" : "bg-amber-500/10 border-amber-500/30 text-amber-100"}`}>
            {verdict}
          </div>

          <div className="space-y-2 text-sm">
            <Row label="From address" value={data?.sender_from} />
            <Row label="Sender email env" value={data?.sender_email_env} />
            <Row label="Sender domain" value={data?.sender_domain} />
            <Row label="Reply-to" value={data?.reply_to} />
            <Row label="API key configured" value={data?.resend_api_key_configured ? `Yes (${data.resend_api_key_prefix})` : "No"} />
            <Row label="Probe status" value={String(data?.domain_probe_status ?? "—")} />
          </div>

          {data?.domain_probe_body && (
            <details className="mt-4">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-600">Raw Resend response</summary>
              <pre className="mt-2 text-[11px] bg-black/40 rounded p-3 text-slate-600 overflow-auto max-h-64">{JSON.stringify(data.domain_probe_body, null, 2)}</pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 border-b border-white/5">
    <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
    <span className="text-xs font-mono text-slate-700 text-right break-all">{value ?? "—"}</span>
  </div>
);


// ---------- View Campaign Modal (preview the sent email) ----------
const ViewCampaignModal = ({ campaignId, onClose }) => {
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Load metadata (subject, recipient counts, etc.)
        const metaResp = await authFetch(`/api/admin/campaigns/${campaignId}`);
        if (!metaResp.ok) throw new Error((await metaResp.json()).detail || "Failed to load campaign");
        const metaData = await metaResp.json();

        // Load the rendered HTML preview (uses same template as real email)
        const htmlResp = await authFetch(`/api/admin/campaigns/${campaignId}/preview`);
        if (!htmlResp.ok) throw new Error("Failed to load preview");
        const html = await htmlResp.text();

        if (!cancelled) {
          setMeta(metaData);
          setPreviewHtml(html);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="rounded-2xl border border-slate-200 bg-white max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="view-campaign-modal"
      >
        <div className="h-1 bg-gradient-to-r from-blue-500 via-cyan-500 to-emerald-500" />
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 truncate">
              {meta?.subject || "Loading…"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {meta ? (
                <>
                  Sent {formatDate(meta.sent_at || meta.created_at)} · by {meta.created_by} ·
                  {" "}{meta.sent_count}/{meta.total_recipients} delivered
                  {meta.failed_count > 0 && <span className="text-rose-600"> · {meta.failed_count} failed</span>}
                </>
              ) : "Preparing preview…"}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 flex-shrink-0" data-testid="view-campaign-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto bg-slate-50">
          {err && (
            <div className="p-6">
              <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
                {err}
              </div>
            </div>
          )}
          {!err && !previewHtml && (
            <div className="py-16 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading preview…
            </div>
          )}
          {previewHtml && (
            <iframe
              title="Email preview"
              srcDoc={previewHtml}
              className="w-full h-[65vh] bg-white border-0"
              sandbox=""
              data-testid="view-campaign-iframe"
            />
          )}
        </div>

        {meta && (
          <div className="px-6 py-3 border-t border-slate-200 bg-white flex items-center justify-between gap-4 text-xs text-slate-500">
            <span>Recipient mode: <strong className="text-slate-700">{meta.recipient_mode || "all"}</strong></span>
            {meta.cta_url && (
              <span className="truncate">
                CTA: <a href={meta.cta_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-mono">{meta.cta_url}</a>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ---------- Resend Confirmation Modal ----------
const ResendConfirmModal = ({ campaign, resending, onCancel, onConfirm }) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={resending ? undefined : onCancel}>
    <div
      className="rounded-2xl border border-slate-200 bg-white max-w-md w-full p-6 shadow-xl"
      onClick={(e) => e.stopPropagation()}
      data-testid="resend-confirm-modal"
    >
      <div className="h-1 rounded-full bg-gradient-to-r from-blue-500 via-cyan-500 to-emerald-500 mb-4 -mt-6 -mx-6 rounded-t-2xl" />
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
          <Send className="w-5 h-5 text-blue-600" />
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-bold text-slate-900">Resend this campaign?</h3>
          <p className="text-sm text-slate-500 mt-0.5">A brand-new broadcast will be logged in your history.</p>
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-5 text-sm space-y-1">
        <div className="flex justify-between gap-3">
          <span className="text-slate-500">Subject:</span>
          <span className="text-slate-900 max-w-[260px] truncate font-medium">{campaign.subject}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">Original sent:</span>
          <span className="text-slate-700">{formatDate(campaign.sent_at || campaign.created_at)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">Original recipients:</span>
          <span className="text-slate-700 font-semibold">{campaign.total_recipients}</span>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-5 leading-relaxed">
        We&rsquo;ll send the same subject, body, hero image, and CTA to a freshly-computed recipient list
        (respecting the original mode and current unsubscribes).
      </p>
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={resending}
          className="flex-1 py-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 text-sm font-medium transition disabled:opacity-50"
          data-testid="resend-cancel-btn"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={resending}
          className="flex-1 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold transition flex items-center justify-center gap-2"
          data-testid="resend-confirm-btn"
        >
          {resending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Yes, resend now</>}
        </button>
      </div>
    </div>
  </div>
);


export default AdminCampaignsPage;
